import * as XLSX from 'xlsx';
import { downloadContentAsFile } from './browserFileHandling';

export type TabularExportFormat = 'CSV' | 'XLS';

export type TabularExportValue = string | number;

interface DownloadOptions {
	fileName: string;
	headers: readonly string[];
	rows: TabularExportValue[][];
}

/** Spreadsheets evaluate a cell whose text starts with one of these, quoted or not. */
const FORMULA_TRIGGERS = ['=', '+', '-', '@', '\t', '\r'];

/**
 * Prefix a formula trigger with an apostrophe so the cell is read as text. A leading `+`/`-` on something
 * that parses as a number is left alone — `-12.50` is a negative price, and importers need it numeric.
 */
export function escapeCsvFormula(value: TabularExportValue): string {
	const text = String(value);
	const first = text[0] ?? '';
	if (!FORMULA_TRIGGERS.includes(first)) return text;
	if ((first === '-' || first === '+') && !Number.isNaN(Number(text))) return text;
	return `'${text}`;
}

/** Quote a CSV field, escaping embedded quotes, so commas/newlines/quotes in names stay intact. */
function csvCell(value: TabularExportValue): string {
	return `"${escapeCsvFormula(value).replaceAll('"', '""')}"`;
}

export function rowsToCsv(headers: readonly string[], rows: TabularExportValue[][]): string {
	return [headers.map(csvCell).join(','), ...rows.map((row) => row.map(csvCell).join(','))].join('\n');
}

const UTF8_BOM = '\uFEFF';

/** `bom` prepends a UTF-8 byte order mark so Excel renders unicode; leave it off for strict importers. */
export function downloadRowsAsCsv({ bom = false, fileName, headers, rows }: DownloadOptions & { bom?: boolean }) {
	const blob = new Blob([`${bom ? UTF8_BOM : ''}${rowsToCsv(headers, rows)}`], { type: 'text/csv;charset=utf-8;' });
	downloadContentAsFile({ fileName: `${fileName}.csv`, uriEncodedContent: URL.createObjectURL(blob) });
}

export function downloadRowsAsXlsx({ fileName, headers, rows, sheetName = 'Export' }: DownloadOptions & { sheetName?: string }) {
	const worksheet = XLSX.utils.aoa_to_sheet([[...headers], ...rows]);
	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
	XLSX.writeFile(workbook, `${fileName}.xlsx`);
}

export function downloadRows({ format, ...options }: DownloadOptions & { format: TabularExportFormat; sheetName?: string }) {
	if (format === 'CSV') downloadRowsAsCsv(options);
	else downloadRowsAsXlsx(options);
}
